import { TRACK_JOB_ENGAGEMENT } from "./Controller/URLConstants";

export const JOB_ENGAGEMENT_QUERY_KEY = "engagement";
const JOB_ENGAGEMENT_HISTORY_KEY = "jobEngagement";
export const JOB_ENGAGEMENT_INTERACTIONS = Object.freeze({
  meaningfulScroll: "meaningful_scroll",
  applicationStarted: "application_started",
});

const TRUSTED_INTERACTIONS = new Set(Object.values(JOB_ENGAGEMENT_INTERACTIONS));
const REPORT_INTERVAL_MS = 5000;
const REPORT_MILESTONES_SECONDS = [10, 15, 30, 60, 120, 300, 600, 900];

function createVisitId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function captureJobEngagementToken({
  locationImpl = typeof window !== "undefined" ? window.location : null,
  historyImpl = typeof window !== "undefined" ? window.history : null,
} = {}) {
  if (!locationImpl?.href || !historyImpl?.replaceState) return null;
  try {
    const url = new URL(locationImpl.href);
    const token = url.searchParams.get(JOB_ENGAGEMENT_QUERY_KEY);
    if (token && token.length <= 2048) {
      url.searchParams.delete(JOB_ENGAGEMENT_QUERY_KEY);
      const previousState = historyImpl.state;
      historyImpl.replaceState(
        {
          ...(previousState && typeof previousState === "object" ?
            previousState : {}),
          [JOB_ENGAGEMENT_HISTORY_KEY]: {path: url.pathname, token},
        },
        "",
        `${url.pathname}${url.search}${url.hash}`,
      );
      return token;
    }

    const captured = historyImpl.state?.[JOB_ENGAGEMENT_HISTORY_KEY];
    return captured?.path === url.pathname ? captured.token : null;
  } catch (_) {
    return null;
  }
}

// Whether the token links a job invite (`jiv`). The payload is read without
// verification, only to decide whether to send the arrival report; the server
// verifies the token.
export function tokenTracksInvite(token) {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return Boolean(JSON.parse(atob(payload)).jiv);
  } catch (_) {
    return false;
  }
}

export function createJobEngagementTracker({
  token,
  jobId,
  endpoint = TRACK_JOB_ENGAGEMENT,
  documentTarget = typeof document !== "undefined" ? document : null,
  windowTarget = typeof window !== "undefined" ? window : null,
  fetchImpl = typeof fetch !== "undefined" ? fetch : null,
  navigatorImpl = typeof navigator !== "undefined" ? navigator : null,
  now = () => Date.now(),
  setIntervalImpl = setInterval,
  clearIntervalImpl = clearInterval,
} = {}) {
  if (!token || !jobId || !documentTarget || !windowTarget) return null;

  const interactions = new Set();
  let activeMilliseconds = 0;
  let lastTickAt = now();
  let lastSentSeconds = -1;
  let lastSentMilestone = 0;
  let visible = documentTarget.visibilityState !== "hidden";
  let intervalId;
  let interactionChanged = false;
  let meaningfulScrollRecorded = false;
  const visitId = createVisitId();

  const accumulate = () => {
    const currentTime = now();
    if (visible) {
      activeMilliseconds += Math.min(
        Math.max(currentTime - lastTickAt, 0),
        30000,
      );
    }
    lastTickAt = currentTime;
    visible = documentTarget.visibilityState !== "hidden";
  };

  const report = (beacon = false, force = false) => {
    accumulate();
    const activeVisibleSeconds = Math.floor(activeMilliseconds / 1000);
    const milestone = REPORT_MILESTONES_SECONDS.reduce(
      (latest, seconds) => seconds <= activeVisibleSeconds ? seconds : latest,
      0,
    );
    if (!force && !interactionChanged && milestone <= lastSentMilestone) {
      return false;
    }
    if (activeVisibleSeconds === lastSentSeconds && !interactionChanged) {
      return false;
    }
    lastSentSeconds = activeVisibleSeconds;
    lastSentMilestone = Math.max(lastSentMilestone, milestone);
    interactionChanged = false;
    const body = JSON.stringify({
      token,
      jobId,
      activeVisibleSeconds,
      interactionTypes: [...interactions],
      visitId,
    });

    if (beacon && navigatorImpl?.sendBeacon) {
      try {
        return navigatorImpl.sendBeacon(
          endpoint,
          new Blob([body], {type: "application/json"}),
        );
      } catch (_) {
        return false;
      }
    }
    if (!fetchImpl) return false;
    Promise.resolve(fetchImpl(endpoint, {
      method: "POST",
      headers: {"Content-Type": "application/json"},
      body,
      keepalive: true,
    })).catch(() => {});
    return true;
  };

  const recordInteraction = (type) => {
    if (!TRUSTED_INTERACTIONS.has(type) || interactions.has(type)) return;
    interactions.add(type);
    interactionChanged = true;
    report();
  };
  const handleVisibilityChange = () => {
    accumulate();
    if (!visible) report(false, true);
  };
  const handleScroll = () => {
    const page = documentTarget.documentElement;
    const scrollable = Math.max(1, page.scrollHeight - windowTarget.innerHeight);
    if (!meaningfulScrollRecorded && windowTarget.scrollY / scrollable >= 0.25) {
      meaningfulScrollRecorded = true;
      recordInteraction(JOB_ENGAGEMENT_INTERACTIONS.meaningfulScroll);
    }
  };
  const handlePageHide = () => report(true, true);

  return {
    start() {
      if (intervalId !== undefined) return;
      // A visit from a job invite email marks the invite as clicked; report
      // the arrival right away so it counts even if the visitor leaves fast.
      if (tokenTracksInvite(token)) report(false, true);
      intervalId = setIntervalImpl(report, REPORT_INTERVAL_MS);
      documentTarget.addEventListener("visibilitychange", handleVisibilityChange);
      windowTarget.addEventListener("scroll", handleScroll, {passive: true});
      windowTarget.addEventListener("pagehide", handlePageHide);
    },
    recordInteraction,
    stop() {
      if (intervalId === undefined) return;
      clearIntervalImpl(intervalId);
      intervalId = undefined;
      documentTarget.removeEventListener("visibilitychange", handleVisibilityChange);
      windowTarget.removeEventListener("scroll", handleScroll);
      windowTarget.removeEventListener("pagehide", handlePageHide);
      report(false, true);
    },
  };
}
